import React, { useEffect, useState } from 'react';
import { TriageErtsAction, TriageItem } from '../services/triageService';
import { splitCompoundDmiDefectList } from '../utils/triage-defects';

/** The reviewer's decision emitted on commit. */
export interface TriageDecision {
  action: 'CREATE' | 'UPDATE';
  targetRecordType: string;
  targetEventId: string | null;
  /** Each entry becomes a separately logged defect after reviewer confirmation. */
  parsedDefects: string[];
  note: string;
  noteDate: string;
  noteTime: string;
  nextAdviseDate: string;
  nextAdviseTime: string;
  ertsDate: string;
  ertsTime: string;
  /** Explicitly distinguishes no ERTS data from a source-requested removal. */
  ertsAction: TriageErtsAction;
}

interface TriageReviewModalProps {
  item: TriageItem;
  canEdit: boolean;
  busy: boolean;
  onCommit: (decision: TriageDecision) => void;
  onDismiss: () => void;
  onClose: () => void;
}

const RECORD_TYPES = ['Unscheduled', 'Scheduled', 'Planned', 'Deferred'];

/** Provides at least the legacy singular discrepancy as a reviewer-editable defect. */
function initialDefectTexts(item: TriageItem): string[] {
  const parsedDefects: string[] = (item.parsedDefects || [])
    .map((defectText: string): string => defectText.trim())
    .filter(Boolean);
  const sourceDefects: string[] =
    parsedDefects.length > 0
      ? parsedDefects
      : item.discrepancy?.trim()
        ? [item.discrepancy.trim()]
        : [];
  return splitCompoundDmiDefectList(sourceDefects);
}

/**
 * Review panel for a single triage item. Shows the parsed email context and
 * lets an edit-access reviewer choose to create a new event or update a matched
 * one, then commit or dismiss. Read-only users see the details but the actions
 * are disabled.
 */
const TriageReviewModal: React.FC<TriageReviewModalProps> = ({
  item,
  canEdit,
  busy,
  onCommit,
  onDismiss,
  onClose,
}) => {
  const hasCandidates = item.matchCandidates.length > 0;
  const [action, setAction] = useState<'CREATE' | 'UPDATE'>(
    item.suggestedAction === 'UPDATE' && hasCandidates ? 'UPDATE' : 'CREATE',
  );
  const [targetRecordType, setTargetRecordType] = useState<string>(
    item.targetRecordType || 'Unscheduled',
  );
  const [targetEventId, setTargetEventId] = useState<string>(
    item.matchedEventId || (hasCandidates ? item.matchCandidates[0].eventId : ''),
  );
  // Per the sender rules, when a time is extracted but no date, default the date
  // to today (the reviewer's current date). The note date/time default to now
  // unless the feed supplied the latest note's timestamp (e.g. ATSG).
  const todayIso = new Date().toISOString().slice(0, 10);
  const nowTimeZulu = new Date().toISOString().slice(11, 16);
  const noteSuppressed: boolean = item.noteSuppressed === true;
  const [parsedDefects, setParsedDefects] = useState<string[]>(() => initialDefectTexts(item));
  const [note, setNote] = useState<string>(noteSuppressed ? '' : item.note || '');
  const [noteDate, setNoteDate] = useState<string>(noteSuppressed ? '' : item.noteDate || todayIso);
  const [noteTime, setNoteTime] = useState<string>(
    noteSuppressed ? '' : item.noteTime || nowTimeZulu,
  );
  useEffect((): void => {
    const sourceNoteSuppressed: boolean = item.noteSuppressed === true;
    setParsedDefects(initialDefectTexts(item));
    setNote(sourceNoteSuppressed ? '' : item.note || '');
    setNoteDate(sourceNoteSuppressed ? '' : item.noteDate || new Date().toISOString().slice(0, 10));
    setNoteTime(
      sourceNoteSuppressed ? '' : item.noteTime || new Date().toISOString().slice(11, 16),
    );
  }, [item]);
  const [nextAdviseDate, setNextAdviseDate] = useState<string>(
    item.nextAdviseDate || (item.nextAdviseTimeZulu ? todayIso : ''),
  );
  const [nextAdviseTime, setNextAdviseTime] = useState<string>(item.nextAdviseTimeZulu || '');
  const [ertsDate, setErtsDate] = useState<string>(
    item.ertsDate || (item.ertsTimeZulu ? todayIso : ''),
  );
  const [ertsTime, setErtsTime] = useState<string>(item.ertsTimeZulu || '');

  const commitDisabled = busy || !canEdit || (action === 'UPDATE' && !targetEventId);

  const updateDefectText = (index: number, value: string): void => {
    setParsedDefects((currentDefects: string[]): string[] =>
      currentDefects.map((defectText: string, defectIndex: number): string =>
        defectIndex === index ? value : defectText,
      ),
    );
  };

  const addDefect = (): void => {
    setParsedDefects((currentDefects: string[]): string[] => [...currentDefects, '']);
  };

  const removeDefect = (index: number): void => {
    setParsedDefects((currentDefects: string[]): string[] =>
      currentDefects.filter(
        (_defectText: string, defectIndex: number): boolean => defectIndex !== index,
      ),
    );
  };

  const handleCommit = (): void => {
    const ertsAction: TriageErtsAction =
      ertsDate.trim() || ertsTime.trim() ? 'set' : item.ertsAction === 'remove' ? 'remove' : 'none';
    onCommit({
      action,
      targetRecordType,
      targetEventId: action === 'UPDATE' ? targetEventId : null,
      parsedDefects: parsedDefects.map((defectText: string): string => defectText.trim()),
      note: note.trim(),
      noteDate,
      noteTime: noteTime.trim(),
      nextAdviseDate,
      nextAdviseTime: nextAdviseTime.trim(),
      ertsDate,
      ertsTime: ertsTime.trim(),
      ertsAction,
    });
  };

  return (
    <div
      className="modal-overlay active"
      role="dialog"
      aria-modal="true"
      aria-label="Triage review"
    >
      <div className="modal triage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Review triage item</h2>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body-scroll">
          <div className="triage-overview">
            <div className="triage-email-context">
              <dl className="triage-detail-grid">
                <dt>Source</dt>
                <dd>
                  {item.sourceEmail}
                  {item.senderVerified ? '' : ' ⚠ unverified'}
                </dd>
                <dt>Subject</dt>
                <dd>{item.subject}</dd>
                <dt>Tail</dt>
                <dd>
                  {item.tailNumber || '—'}
                  {item.tailInFleet ? '' : ' ⚠ not in fleet'}
                </dd>
                <dt>Carrier</dt>
                <dd>{item.carrier || '—'}</dd>
                <dt>Aircraft</dt>
                <dd>{item.aircraftType || '—'}</dd>
                <dt>Parse</dt>
                <dd>{item.parseStatus}</dd>
              </dl>

              {item.parseWarnings.length > 0 && (
                <ul className="triage-warnings" role="alert">
                  {item.parseWarnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="triage-decision-controls">
              <fieldset className="triage-action" disabled={!canEdit}>
                <legend>Action</legend>
                <label>
                  <input
                    type="radio"
                    name="triage-action"
                    checked={action === 'CREATE'}
                    onChange={() => setAction('CREATE')}
                  />
                  Create new event
                </label>
                <label className={hasCandidates ? '' : 'triage-disabled'}>
                  <input
                    type="radio"
                    name="triage-action"
                    checked={action === 'UPDATE'}
                    disabled={!hasCandidates}
                    onChange={() => setAction('UPDATE')}
                  />
                  Update existing event
                </label>
              </fieldset>

              {action === 'CREATE' && (
                <div className="triage-field">
                  <label htmlFor="triage-record-type">Record type</label>
                  <select
                    id="triage-record-type"
                    value={targetRecordType}
                    disabled={!canEdit}
                    onChange={(e) => setTargetRecordType(e.target.value)}
                  >
                    {RECORD_TYPES.map((rt) => (
                      <option key={rt} value={rt}>
                        {rt}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {action === 'UPDATE' && hasCandidates && (
                <div className="triage-field">
                  <label htmlFor="triage-target-event">Target event</label>
                  <select
                    id="triage-target-event"
                    value={targetEventId}
                    disabled={!canEdit}
                    onChange={(e) => setTargetEventId(e.target.value)}
                  >
                    {item.matchCandidates.map((c) => (
                      <option key={c.eventId} value={c.eventId}>
                        {c.recordType} · {c.status} · {c.station} · {c.discrepancy.slice(0, 60)}
                      </option>
                    ))}
                  </select>
                  <p className="triage-hint">
                    The full existing record is loaded and re-saved; detailed edits can be made
                    afterward from the event list.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="triage-field">
            <span className="field-input-label">Defects</span>
            <p className="triage-hint">
              Each entry is saved as a separate defect. Confirm, edit, remove, or add entries before
              opening the event.
            </p>
            {parsedDefects.map((defectText: string, index: number) => (
              <div className="triage-defect-row" key={`triage-defect-${index}`}>
                <label htmlFor={`triage-defect-${index}`}>Defect {index + 1}</label>
                <textarea
                  id={`triage-defect-${index}`}
                  value={defectText}
                  disabled={!canEdit}
                  placeholder="Describe one defect from the email…"
                  onChange={(event) => updateDefectText(index, event.target.value)}
                />
                <button
                  className="modal-btn"
                  type="button"
                  disabled={!canEdit}
                  onClick={() => removeDefect(index)}
                >
                  Remove
                </button>
              </div>
            ))}
            <button className="modal-btn" type="button" disabled={!canEdit} onClick={addDefect}>
              Add defect
            </button>
          </div>

          <div className="triage-field">
            <label htmlFor="triage-note">Note</label>
            <textarea
              id="triage-note"
              value={note}
              disabled={!canEdit}
              placeholder="Note from the email…"
              onChange={(e) => setNote(e.target.value)}
            />

            <div className="triage-timing-grid">
              <div className="triage-timing-field">
                <label htmlFor="triage-note-date">Note date / time</label>
                <input
                  id="triage-note-date"
                  type="date"
                  value={noteDate}
                  disabled={!canEdit}
                  onChange={(e) => setNoteDate(e.target.value)}
                />
                <input
                  type="text"
                  value={noteTime}
                  disabled={!canEdit}
                  placeholder="HH:MM Z"
                  maxLength={5}
                  aria-label="Note Zulu time"
                  onChange={(e) => setNoteTime(e.target.value)}
                />
              </div>

              <div className="triage-timing-field">
                <label htmlFor="triage-next-advise-date">Next Advise</label>
                <input
                  id="triage-next-advise-date"
                  type="date"
                  value={nextAdviseDate}
                  disabled={!canEdit}
                  onChange={(e) => setNextAdviseDate(e.target.value)}
                />
                <input
                  type="text"
                  value={nextAdviseTime}
                  disabled={!canEdit}
                  placeholder="HH:MM Z"
                  maxLength={5}
                  aria-label="Next Advise Zulu time"
                  onChange={(e) => setNextAdviseTime(e.target.value)}
                />
              </div>

              <div className="triage-timing-field">
                <label htmlFor="triage-erts-date">ERTS</label>
                <input
                  id="triage-erts-date"
                  type="date"
                  value={ertsDate}
                  disabled={!canEdit}
                  onChange={(e) => setErtsDate(e.target.value)}
                />
                <input
                  type="text"
                  value={ertsTime}
                  disabled={!canEdit}
                  placeholder="HH:MM Z"
                  maxLength={5}
                  aria-label="ERTS Zulu time"
                  onChange={(e) => setErtsTime(e.target.value)}
                />
              </div>
            </div>
          </div>

          {!canEdit && (
            <p className="triage-hint" role="note">
              You have read-only access. Commit and dismiss are disabled.
            </p>
          )}
        </div>

        <div className="modal-footer triage-footer">
          <button className="modal-btn modal-btn-cancel" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="modal-btn"
            type="button"
            disabled={busy || !canEdit}
            onClick={onDismiss}
          >
            Dismiss
          </button>
          <button
            className="modal-btn modal-btn-save"
            type="button"
            disabled={commitDisabled}
            onClick={handleCommit}
          >
            {action === 'CREATE' ? 'Create event' : 'Update event'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TriageReviewModal;
